import React from 'react'
import { Form as RsForm, Row, Col, Label } from 'reactstrap'
import { withDialogs, dialogTypes } from 'decorators'
import { ValidationResult, ErrorValidationIcon, Validator } from 'components/validation'
import { Select } from 'components/select'
import { Button, ConfirmButton } from 'components/buttons'
import { Grid, Column, actionCell, lookupCell } from 'components/grid'
import { api as suppliersApi } from 'store/modules/lookups/suppliers/api'
import { DatePicker } from 'components/datePicker'
import { orderStatus } from 'enums/orderStatus'
import { paymentType } from 'enums/paymentType'
import { useStyles } from './form.css'
import { AddItemButton } from 'components/buttons'
import { api as currenciesApi } from 'store/modules/lookups/currencies'
import { Input } from 'components/inputs'

const NEW_ORDERED_WINE = {
  id: 0,
  wine: null,
  units: 0,
  acquisitionPrice: 0
}

const Form = (props) => {
  const classes = useStyles()
  const handleSent = React.useCallback(() => {
    props.createOrderedBottles({ id: props.data.entity.id })
  }, [props])
  const handleCurrencyChange = React.useCallback((value, options) => {
    if (value && value.name === 'CZK') {
      props.updateEntity('exchangeRate')(1.0, options)
    }
    return props.updateEntity('currency')(value, options)
  }, [props.updateEntity])
  const { disabled, data, updateEntity, localize } = props
  const { entity, saving, creatingBottles } = data
  if (!entity) { return null }

  const actionProps = ({
    onDelete: (_, index) => updateEntity('orderedWines')(entity.orderedWines.filter((_, idx) => idx !== index)),
    confirmMessage: x => localize('orderedWineDialog.confirmDeleteWine')
  })
  const editOrderedWine = ({ dataItem }) => {
    
    const dataIndex = entity.orderedWines.indexOf(dataItem)
    props.showDialog({
      dialogType: dialogTypes.orderedWine,
      context: {
        initialState: dataItem,
        index: dataIndex,
        onSaved: ({ entity: orderedWine, index }) => {
          const newOrderedWines = (index === -1 ? [...entity.orderedWines, orderedWine] : entity.orderedWines.map((x, idx) => idx !== index ? x : orderedWine))
          updateEntity('orderedWines')(newOrderedWines)
        }
      }
    })
  }
  
  const canModify = entity.status === orderStatus.created
  const editableOrderStatus = { ...orderStatus, values: orderStatus.values.filter(x => x !== orderStatus.created )}
  return (
    <RsForm>
      <Row>
        <Col sm={4}>
          <Label>{localize('orders.supplier')}</Label>
          <AddItemButton dialogType={dialogTypes.supplierDetail} disabled={disabled || !canModify} onCreatedEntity={updateEntity('supplier')}>
            <Validator name='supplier' value={entity.supplier} required disabled={disabled || !canModify}>
              <Select
                dataSource={suppliersApi.get}
                value={entity.supplier}
                onChange={updateEntity('supplier')}
                disabled={disabled || !canModify}
                />
            </Validator>
          </AddItemButton>
        </Col>
        <Col sm={2}>
          <Label>{localize('orders.dateOfOrder')}</Label>
          <Validator name='dateOfOrder' value={entity.dateOfOrder} required disabled={disabled} style={{ width: 205 }}>
            <DatePicker value={entity.dateOfOrder} onChange={updateEntity('dateOfOrder')} />
          </Validator>
        </Col>
        <Col sm={3}>
          <Label>{localize('orders.currency')}</Label>
          <AddItemButton dialogType={dialogTypes.currencyDetail} disabled={disabled || !canModify} onCreatedEntity={updateEntity('currency')}>
            <Validator name='currency' value={entity.currency} disabled={disabled || !canModify}>
              <Select
                dataSource={currenciesApi.get}
                value={entity.currency}
                onChange={handleCurrencyChange}
                nullable
                disabled={disabled || !canModify} />
            </Validator>
          </AddItemButton>
        </Col>
        <Col sm={3}>
          <Label>{localize('orders.exchangeRate')}</Label>
          <Validator name='exchangeRate' value={entity.exchangeRate} required={entity.currency !== null} disabled={disabled}>
            <Input value={entity.exchangeRate} isNumeric onChange={updateEntity('exchangeRate')}
              placeholder={localize('orders.exchangeRatePlaceholder')} disabled={disabled || !canModify || entity.currency === null}
              />
          </Validator>
        </Col>
      </Row>
      <Row>
        <Col sm={4}>
          <Label>{localize('orders.paymentType')}</Label>
          <Select value={entity.paymentType} onChange={updateEntity('paymentType')}
              nullable
              placeholder={localize('orders.paymentType')} disabled={disabled}
              enum={paymentType} />
        </Col>
        <Col sm={2}>
          <Label>{localize('orders.dateOfPayment')}</Label>
          <Validator name='dateOfPayment' value={entity.dateOfPayment} disabled={disabled} style={{ width: 205 }}>
            <DatePicker value={entity.dateOfPayment} onChange={updateEntity('dateOfPayment')} />
          </Validator>
        </Col>
      </Row>
      {entity.id > 0 && entity.status !== orderStatus.created && <Row>
        <Col sm='6'>
          <Label>{localize('orders.status')}</Label>
          <Validator name='status' value={entity.status} required min={1} disabled={disabled}>
            <Select value={entity.status} onChange={updateEntity('status')}
              placeholder={localize('orders.status')} disabled={disabled}
              enum={editableOrderStatus} />
          </Validator>
        </Col>
      </Row>}
      <Row><Col><hr /></Col></Row>
      <Row>
        <Col>
          <Label>{localize('orders.orderedWines')}</Label>
          <Validator name='orderedWines' value={entity.orderedWines} required disabled={disabled} style={{ width: 170 }}>
            <Button color='primary' icon='plus' disabled={!canModify || creatingBottles} onClick={() => editOrderedWine({ dataItem: NEW_ORDERED_WINE })}>{localize('orders.addWine')}</Button>
          </Validator>
          
          <div className={classes.grid}>
            <Grid
              data={entity.orderedWines}
              onRowClick={canModify ? editOrderedWine : null}
              pageable={false}
              filterable={false}>
              <Column field='wine' title={localize('orders.wine')} cell={lookupCell()} />
              <Column field='units' title={localize('orders.totalUnits')} filter='numeric' width='200px' />
              <Column field='acquisitionPrice' title={localize('orders.price')} filter='numeric' width='200px' />
              <Column cell={actionCell({ actionProps: (canModify || !creatingBottles) ? actionProps : {} })} width='100px' filterable={false} sortable={false} />
            </Grid>
          </div>
        </Col>
      </Row>
      {entity.status === orderStatus.created && entity.id > 0 && <Row>
        <Col className={classes.sentOrder}>
          <ValidationResult render={({ isValid }) =>
            <div style={{ display: 'inline', position: 'relative' }}>
              <ConfirmButton
                onConfirm={handleSent}
                confirmMessage={localize('orders.confirmSentOrder')}
                minWidth='inherit'
                disabled={!isValid || entity.isDirty === true || saving || creatingBottles}
                isLoading={saving || creatingBottles}
                title={localize('orders.sentOrderTooltip')}
                confirmTitle={localize('common.confirm')}
                color='primary'
                width={250}>{localize('orders.sentOrder')}</ConfirmButton>
              <ErrorValidationIcon isValid={isValid} title={localize('common.formContainsValidationErrors')} backgroundColor='transparent' iconProps={{ style: { fontSize: 16 } }} />
            </div>
          } />
        </Col>
      </Row>}
    </RsForm>
  )
}

export default withDialogs(Form)